import type { FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import type { RegisterOptions } from "react-hook-form";
import { useForm } from "react-hook-form";

import { Form } from "@ui/components/Form";
import { Recaptchabar } from "@ui/components/Recaptchabar";
import { useTranslations } from "@ui/providers/useTranslations";
import { displayToastError } from "@ui/utils/displayToastError";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import type { OtPurpose, Steps } from "@jti/fe-common";
import type { RestResponseType } from "@jti/rest-api";
import { CasPurposesApi, OmpApi, useUserProfile } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { SuccessScreen } from "./components/SuccessScreen/SuccessScreen";
import type { ContactFormValues } from "./helpers/helpers";
import {
  getConvertedChildrenData,
  getSuccessFields,
  removeWhitespacesFromPhoneNumber,
  replaceSelectValuesWithString,
  transformDataWithConsents,
  updateCheckboxValue,
} from "./helpers/helpers";

import styles from "./ContactForm.module.scss";

export type FormInputType = {
  id: string;
  inputType: "textinput" | "textarea" | "checkbox" | "select" | "option" | "consentcheckbox";
  label?: string;
  placeholder?: string;
  required?: boolean;
  minLength?: string;
  maxLength?: string;
  disclaimer?: string;
  requiredMessage?: string;
  errorMessage?: string;
  validation?: string;
  matchingFieldId?: string;
  fieldDoesNotMatchMessage?: string;
  options?: Array<{ value: string; label: string }>;
  checked?: boolean;
  translatedName?: string;
  validationRules?: RegisterOptions;
  displayConsent?: "always" | "onlyNotLoggedIn" | "onlyLoggedIn";
  dataTestId?: string;
  consent?: Steps;
};

export type PreFilledFieldsType = {
  nameFieldName?: string | undefined;
  surnameFieldName?: string | undefined;
  emailFieldName?: string | undefined;
  confirmEmailFieldName?: string | undefined;
  phoneNumberFieldName?: string | undefined;
};

export type ContactFormProps = {
  fields?: Array<FormInputType>;
  sendButtonLabel?: string;
  description?: string;
  title?: string;
  type?: string;
  hiddenFields?: Array<{ key: string; value: string }>;
  serverErrorMessage?: string;
  formDisclaimer?: string;
  successScreenButtonLabel?: string;
  successScreenAlertButtonLabel?: string;
  successScreenTitle?: string;
  successScreenHref?: string;
  successScreenDescription?: string;
  onSuccess?: () => void;
  className?: string;
  isPrefilled?: boolean;
  isPrefilledDisabled?: boolean;
  preFilledFields?: PreFilledFieldsType;
  skipConsentRetrieval?: boolean;
};

export type OmpResponse = RestResponseType & {
  errors?: Array<{ message: string }>;
};

export const ContactForm: FC<PropsWithChildren<ContactFormProps>> = ({
  children,
  fields,
  sendButtonLabel,
  description,
  title,
  type,
  hiddenFields,
  serverErrorMessage,
  formDisclaimer,
  successScreenTitle,
  successScreenButtonLabel,
  successScreenAlertButtonLabel,
  successScreenHref,
  successScreenDescription,
  isPrefilled = false,
  isPrefilledDisabled = false,
  preFilledFields,
  skipConsentRetrieval = false,
  onSuccess = null,
  className = "",
}) => {
  const userProfile = useUserProfile();
  const [convertedChildrenData, setConvertedChildrenData] = useState(children);
  const { reCaptchaEnabled, brandingInformationEnabled } = useConfig();
  const mappedHiddenFields = hiddenFields?.reduce<ContactFormValues>((acc, field) => {
    acc[field.key] = field.value;
    return acc;
  }, {});
  const defaultValues: ContactFormValues = {
    ...fields?.reduce<ContactFormValues>((acc, field) => {
      if (field.checked) {
        acc[field.id] = true;
        return acc;
      }
      acc[field.id] = "";
      return acc;
    }, {}),
    ...mappedHiddenFields,
  };
  const form = useForm<ContactFormValues>({
    mode: "onTouched",
    defaultValues,
  });
  const { reset, trigger, getValues } = form;
  const [consent, setConsent] = useState<OtPurpose[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [successFields, setSuccessFields] = useState<Array<{ id?: string; label: string; value?: string }>>([]);
  const { __, dictionary } = useTranslations();

  const onSubmit = async () => {
    const values = getValues();
    setIsLoading(true);
    trigger(Object.keys(values));

    // Temporary solution before DESP-69708 goes live and all TextInputs used for phone number can be replaced with InternationalPhoneInput
    const processedValues = updateCheckboxValue(removeWhitespacesFromPhoneNumber(values));

    try {
      const responseData: OmpResponse = await OmpApi.init().sendMessage(
        transformDataWithConsents(replaceSelectValuesWithString(processedValues), consent),
        type
      );

      setIsLoading(false);
      dispatchTrackingEvent(TrackingEvents.TrackingEvent, { event: "contact_form_submit" });

      if (responseData?.errors && serverErrorMessage) {
        toast.error(serverErrorMessage);
        return;
      }

      const rawFields = getSuccessFields(fields ?? [], processedValues);
      const filteredFields = rawFields
        .filter((f): f is NonNullable<typeof f> => f !== undefined)
        .map(({ id, label, value }) => ({ id, label: label ?? "", value }));
      setSuccessFields(filteredFields);
      setShowSuccessScreen(true);
      if (onSuccess) {
        onSuccess();
      }

      reset();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      displayToastError(error, serverErrorMessage, dictionary);
    }
  };

  const resetContactForm = () => {
    setSuccessFields([]);
    setShowSuccessScreen(false);
  };

  useEffect(() => {
    if (skipConsentRetrieval) return;

    CasPurposesApi.init()
      .getPurposesByStep("contact_form")
      .then((response) => {
        setConsent(response);
      })
      .catch((error) => {
        displayToastError(error, undefined, dictionary);
      });
  }, [skipConsentRetrieval]);

  useEffect(() => {
    if (isPrefilled && !!userProfile && !!preFilledFields) {
      const convertedChildrenData = getConvertedChildrenData(children, preFilledFields, isPrefilledDisabled);
      setConvertedChildrenData(convertedChildrenData);
      const { surnameFieldName, nameFieldName, emailFieldName, confirmEmailFieldName, phoneNumberFieldName } =
        preFilledFields;
      reset({
        ...(!!nameFieldName && { [nameFieldName]: userProfile.firstname }),
        ...(!!surnameFieldName && { [surnameFieldName]: userProfile.lastname }),
        ...(!!emailFieldName && { [emailFieldName]: userProfile.email }),
        ...(!!confirmEmailFieldName && { [confirmEmailFieldName]: userProfile.email }),
        ...(!!phoneNumberFieldName && { [phoneNumberFieldName]: userProfile.telephone }),
        ...mappedHiddenFields,
      });
    }
  }, [isPrefilled, reset, !!userProfile, !!preFilledFields]);

  const buttonProps = {
    disabled: !form.formState.isValid,
    type: "submit" as const,
    pending: isLoading,
    children: sendButtonLabel || __("contact.button.send"),
  };

  return (
    <div className={classNames(styles.formWrapper, className)}>
      <div className={styles.headingWrapper}>
        <h2 data-testid="contact-form-title" className={styles.title}>
          {title}
        </h2>
        <p data-testid="contact-form-description" className={styles.description}>
          {description}
        </p>
      </div>
      {showSuccessScreen ? (
        <SuccessScreen
          successScreenTitle={successScreenTitle}
          successScreenButtonLabel={successScreenButtonLabel ?? ""}
          successScreenAlertButtonLabel={successScreenAlertButtonLabel ?? ""}
          successFields={successFields}
          successScreenHref={successScreenHref ?? ""}
          successScreenDescription={successScreenDescription}
          resetContactForm={resetContactForm}
        />
      ) : (
        <>
          <Form className={styles.form} dataTestId="contactForm" form={form} onSubmit={onSubmit}>
            {convertedChildrenData}
            <p className={styles.text}>{formDisclaimer}</p>
            <div className={styles.buttonWrapper}>
              <Button data-testid="send-button-mobile" className={styles.mobileButton} size="sm" {...buttonProps}>
                {buttonProps.children}
              </Button>
              <Button data-testid="send-button" className={styles.button} size="lg" {...buttonProps}>
                {buttonProps.children}
              </Button>
            </div>
          </Form>
          {reCaptchaEnabled && brandingInformationEnabled && <Recaptchabar />}
        </>
      )}
    </div>
  );
};
